<script setup>
import { computed, ref } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Evolucao Diaria',
  },
});

const showResolved = ref(true);
const showConversations = ref(true);

const sortedData = computed(() => {
  if (!props.data || props.data.length === 0) return [];
  return [...props.data].sort((a, b) => new Date(a.date) - new Date(b.date));
});

const maxValue = computed(() => {
  if (sortedData.value.length === 0) return 1;
  let max = 0;
  sortedData.value.forEach(d => {
    if (showConversations.value && d.conversations > max) max = d.conversations;
    if (showResolved.value && d.resolved > max) max = d.resolved;
  });
  return max || 1;
});

const totals = computed(() => {
  return sortedData.value.reduce(
    (acc, d) => {
      acc.conversations += d.conversations || 0;
      acc.resolved += d.resolved || 0;
      return acc;
    },
    { conversations: 0, resolved: 0 }
  );
});

const avgResolutionRate = computed(() => {
  if (totals.value.conversations === 0) return 0;
  return ((totals.value.resolved / totals.value.conversations) * 100).toFixed(
    1
  );
});

function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function getBarHeight(value) {
  return `${(value / maxValue.value) * 100}%`;
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-trending-up text-lg text-n-blue-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>

      <!-- Toggle buttons -->
      <div class="flex items-center gap-2">
        <button
          class="flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors"
          :class="
            showConversations
              ? 'bg-n-blue-3 text-n-blue-11'
              : 'bg-n-alpha-1 text-n-slate-11'
          "
          @click="showConversations = !showConversations"
        >
          <span class="w-2 h-2 rounded bg-n-blue-9" />
          Criadas
        </button>
        <button
          class="flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors"
          :class="
            showResolved
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-alpha-1 text-n-slate-11'
          "
          @click="showResolved = !showResolved"
        >
          <span class="w-2 h-2 rounded bg-n-teal-9" />
          Resolvidas
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="sortedData.length === 0"
      class="flex items-center justify-center h-48 text-n-slate-11"
    >
      <span class="text-sm">Sem dados disponiveis</span>
    </div>

    <!-- Chart -->
    <div v-else class="flex flex-col">
      <!-- Bars -->
      <div class="flex items-end gap-1 h-40">
        <div
          v-for="day in sortedData"
          :key="day.date"
          class="flex-1 flex gap-0.5 items-end h-full"
        >
          <div
            v-if="showConversations"
            class="flex-1 bg-n-blue-9 rounded-t transition-all duration-300"
            :style="{ height: getBarHeight(day.conversations) }"
            :title="`${formatDate(day.date)}: ${day.conversations} criadas`"
          />
          <div
            v-if="showResolved"
            class="flex-1 bg-n-teal-9 rounded-t transition-all duration-300"
            :style="{ height: getBarHeight(day.resolved) }"
            :title="`${formatDate(day.date)}: ${day.resolved} resolvidas`"
          />
        </div>
      </div>

      <!-- Date labels -->
      <div class="flex gap-1 mt-2 pt-2 border-t border-n-weak overflow-x-auto">
        <div
          v-for="(day, index) in sortedData"
          :key="`label-${day.date}`"
          class="flex-1 text-center min-w-0"
        >
          <span
            v-if="
              index % Math.ceil(sortedData.length / 7) === 0 ||
              sortedData.length <= 7
            "
            class="text-[10px] text-n-slate-10 whitespace-nowrap"
          >
            {{ formatDate(day.date) }}
          </span>
        </div>
      </div>

      <!-- Summary -->
      <div
        class="flex items-center justify-between mt-4 pt-3 border-t border-n-weak"
      >
        <div class="flex items-center gap-4 text-sm">
          <div class="flex items-center gap-2">
            <span class="text-n-slate-11">Criadas:</span>
            <span class="font-bold text-n-blue-11">{{
              totals.conversations
            }}</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-n-slate-11">Resolvidas:</span>
            <span class="font-bold text-n-teal-11">{{ totals.resolved }}</span>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-xs text-n-slate-11">Taxa:</span>
          <span class="text-lg font-bold text-n-teal-11">{{ avgResolutionRate }}%</span>
        </div>
      </div>
    </div>
  </div>
</template>
